import { yupResolver } from "@hookform/resolvers/yup";
import { useSnackbar } from "notistack";
import { Controller, useForm } from "react-hook-form";
import { Dispositivi } from "../prismaTypes";
import * as yup from "yup";
import axios, { AxiosError } from "axios";
import { useMutation } from "react-query";
import { API_URL } from "../consts";
import { useEffect, useState } from "react";
import { queryClient } from "../App";
import { changeDatesToUTC } from "../utils/dateUtils";
import {
  FormControlLabel,
  Checkbox,
  TextField,
  Grid,
  Button,
  DialogContent,
  FormGroup,
  FormLabel,
} from "@mui/material";
import DialogActions from "@mui/material/DialogActions";

const schema = yup
  .object({
    anticipoAccensione: yup
      .number()
      .nullable(true)
      .transform((_, val) => (val !== "" ? Number(val) : null)),
    durata: yup
      .number()
      .nullable(true)
      .transform((_, val) => (val !== "" ? Number(val) : null)),
  })
  .required();

export function Dispositivo(props: {
  clubId: number;
  risorseId?: number;
  dispositivo: Dispositivi | undefined;
  handleClose: () => void;
}) {
  const { clubId, risorseId, handleClose, dispositivo } = props;
  const { enqueueSnackbar } = useSnackbar();
  const [animations, setAnimations] = useState(dispositivo === undefined);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let timeout = 0;
    if (loaded) timeout = window.setTimeout(() => setAnimations(true), 1);
    return () => {
      if (timeout !== 0) clearTimeout(timeout);
    };
  }, [loaded]);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<Dispositivi>({
    mode: "all",
    resolver: yupResolver(schema),
  });

  const { mutate: mutateCreate } = useMutation(
    (data: Dispositivi) => {
      return axios.post(API_URL + "dispositivi", data);
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries();
        enqueueSnackbar("Dispositivo Creato", { variant: "success" });
        handleClose();
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(error?.response?.data?.error ?? "Si è verificato un errore!", {
          variant: "error",
        });
      },
    }
  );

  const { mutate: mutateUpdate } = useMutation(
    (data: Dispositivi) => {
      return axios.patch(API_URL + "dispositivi/" + dispositivo?.id ?? -1, data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries();
        enqueueSnackbar("Dispositivo Aggiornato", { variant: "success" });
        handleClose();
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(error?.response?.data?.error ?? "Si è verificato un errore!", {
          variant: "error",
        });
      },
    }
  );

  const onSubmit = (data: Dispositivi) => {
    changeDatesToUTC(data);
    data = { ...data, idClub: clubId, idRisorse: risorseId ?? -1 };
    if (dispositivo !== undefined) mutateUpdate({ ...data, id: dispositivo.id });
    else mutateCreate({ ...data });
  };

  useEffect(() => {
    if (!dispositivo) return;
    setValue("id", dispositivo.id);
    setValue("idClub", dispositivo.idClub);
    setValue("loraDevice", dispositivo.loraDevice);
    setValue("loraGroup", dispositivo.loraGroup);
    setValue("descrizione", dispositivo.descrizione);
    setValue("idRisorse", dispositivo.idRisorse);
    setValue("suddividiSlots", dispositivo.suddividiSlots);
    setValue("accensioneLuci", dispositivo.accensioneLuci);
    setValue("accensionePrenotazione", dispositivo.accensionePrenotazione);
    setValue("accensioneRiscaldamento", dispositivo.accensioneRiscaldamento);
    setValue("fine", dispositivo.fine);

    if (dispositivo.anticipoAccensione !== null)
      setValue("anticipoAccensione", dispositivo.anticipoAccensione);

    if (dispositivo.durata !== null) setValue("durata", dispositivo.durata);

    setLoaded(true);
  }, [dispositivo, setValue]);

  if (!animations) return <></>;
  return (
    <form onSubmit={handleSubmit(onSubmit)} className={animations ? "" : "noAnimation"}>
      <DialogContent>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Controller
              name="descrizione"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  variant="standard"
                  label="Descrizione"
                  error={Boolean(errors.descrizione)}
                  helperText={errors.descrizione?.message ?? " "}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="loraDevice"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  variant="standard"
                  label="Lora Device"
                  error={Boolean(errors.loraDevice)}
                  helperText={errors.loraDevice?.message ?? " "}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="loraGroup"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  variant="standard"
                  label="Lora Group"
                  error={Boolean(errors.loraGroup)}
                  helperText={errors.loraGroup?.message ?? " "}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="suddividiSlots"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={(v) => setValue("suddividiSlots", v.target.checked)}
                    />
                  }
                  label="Suddividi Slots"
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="fine"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={(v) => setValue("fine", v.target.checked)}
                    />
                  }
                  label="Accendi alla fine"
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="anticipoAccensione"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  variant="standard"
                  label="Anticipo accensione"
                  type="number"
                  error={Boolean(errors.anticipoAccensione)}
                  helperText={errors.anticipoAccensione?.message ?? " "}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="durata"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  variant="standard"
                  label="Durata"
                  type="number"
                  error={Boolean(errors.durata)}
                  helperText={errors.durata?.message ?? " "}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <FormGroup>
              <FormLabel>Tipologia accensione</FormLabel>
              <Controller
                name="accensionePrenotazione"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(v) => setValue("accensionePrenotazione", v.target.checked)}
                      />
                    }
                    label="Accensione con prenotazione"
                  />
                )}
              />
              <Controller
                name="accensioneLuci"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(v) => setValue("accensioneLuci", v.target.checked)}
                      />
                    }
                    label="Accensione Luci"
                  />
                )}
              />
              <Controller
                name="accensioneRiscaldamento"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(v) => setValue("accensioneRiscaldamento", v.target.checked)}
                      />
                    }
                    label="Accensione Riscaldamento"
                  />
                )}
              />
            </FormGroup>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} autoFocus>
          Chiudi
        </Button>
        <Button type="submit" autoFocus>
          Salva
        </Button>
      </DialogActions>
    </form>
  );
}
